import * as config from "../constants.js";
import { gameState } from "./gameState-core.js";
import { addNotification } from "./notifications.js";
import { getAllItems, getAllArtifacts } from "./util.js";
import { getUtilityArtifactBonus } from "./character-core.js";
import { debug, isDebug, CH } from './debugLog.js';

// ============================================================================
// ITEM DROP WEIGHT CONFIGURATION
// ============================================================================

/**
 * Per-item drop weights (relative selection weights within a rarity pool).
 * Maps item IDs to weights; items without an entry default to 1.0.
 * A weight of 0.06 vs 0.75 means the second item is picked ~12x as often -
 * weights shape the MIX of items dropped, not the total quantity.
 * Sourced from itemsRarities dropChance.perItem at boot (game and simulator).
 */
export let itemDropRateMultipliers = {};

/**
 * Set per-item drop weights
 * @param {Object} multipliers - Map of itemId -> weight
 */
export function setItemDropRateMultipliers(multipliers = {}) {
  itemDropRateMultipliers = { ...multipliers };
}

/**
 * Get drop weight for a specific item (1.0 when unconfigured)
 */
export function getItemDropRateMultiplier(itemId) {
  return itemDropRateMultipliers[itemId] !== undefined ? itemDropRateMultipliers[itemId] : 1.0;
}

/**
 * Pick one item from a pool, weighted by per-item drop weights.
 * Falls back to uniform selection when the pool has no configured weights.
 * @param {Array<string>} itemIds - Candidate item IDs
 * @returns {string|null} Selected item ID, or null for an empty pool
 */
export function pickWeightedItem(itemIds, boost = null) {
  if (!itemIds || itemIds.length === 0) return null;

  // Optional selection boost: items passing boost.predicate get their
  // weight multiplied (e.g. the Relic Resonance Tuner favoring relics
  // within a mixed pool without inflating the rest of it)
  const weightOf = boost
    ? id => getItemDropRateMultiplier(id) * (boost.predicate(id) ? boost.multiplier : 1)
    : getItemDropRateMultiplier;

  const totalWeight = itemIds.reduce((sum, id) => sum + weightOf(id), 0);
  if (totalWeight <= 0) {
    return itemIds[Math.floor(Math.random() * itemIds.length)];
  }

  let roll = Math.random() * totalWeight;
  for (const id of itemIds) {
    roll -= weightOf(id);
    if (roll <= 0) return id;
  }
  return itemIds[itemIds.length - 1];
}

// ============================================================================
// CREDITS SCALING CONFIGURATION
// ============================================================================

/**
 * Configuration for credit scaling
 * Allows adjusting credit multipliers globally and per-tier
 */
export let creditsScaling = {
  globalMultiplier: 1.0,
  tierMultipliers: {
    1: 1.0,
    2: 1.0,
    3: 1.0
  }
};

/**
 * Set credits scaling configuration
 * @param {Object} config - Configuration object with globalMultiplier and tierMultipliers
 */
export function setCreditsScaling(config = {}) {
  creditsScaling = {
    globalMultiplier: config.globalMultiplier !== undefined ? config.globalMultiplier : 1.0,
    tierMultipliers: config.tierMultipliers || {
      1: 1.0,
      2: 1.0,
      3: 1.0
    }
  };
}

/**
 * Get credit multiplier for a specific tier
 * Combines global multiplier with tier-specific multiplier
 */
export function getCreditsMultiplier(tier = 1) {
  const tierMultiplier = creditsScaling.tierMultipliers[tier] || 1.0;
  return creditsScaling.globalMultiplier * tierMultiplier;
}

/**
 * Apply credit scaling to earned credits.
 * Single choke-point for raid credit awards: every award site (base room
 * credit, completion bonus, Field Intelligence, raid-end results) and its
 * raid-log message route through here, so the Data Cache Drive's
 * currency-bonus stays consistent between what's narrated and what's banked.
 */
export function applyCreditsScaling(creditsEarned, tier = 1) {
  const multiplier = getCreditsMultiplier(tier);
  const currencyBonus = 1 + getUtilityArtifactBonus('currency-bonus', gameState.character) / 100;
  return Math.floor(creditsEarned * multiplier * currencyBonus);
}

// ============================================================================
// CRAFTING COST SCALING CONFIGURATION
// ============================================================================

/**
 * Configuration for crafting cost scaling
 * Allows adjusting crafting costs globally and per-rarity
 */
export let craftingCostScaling = {
  globalMultiplier: 1.0,
  rarityMultipliers: {
    common: 1.0,
    uncommon: 1.0,
    rare: 1.0,
    epic: 1.0,
    legendary: 1.0,
    unique: 1.0,
    artifact: 1.0
  }
};

/**
 * Set crafting cost scaling configuration
 * @param {Object} config - Configuration object with globalMultiplier and rarityMultipliers
 */
export function setCraftingCostScaling(config = {}) {
  craftingCostScaling = {
    globalMultiplier: config.globalMultiplier !== undefined ? config.globalMultiplier : 1.0,
    rarityMultipliers: config.rarityMultipliers || {
      common: 1.0,
      uncommon: 1.0,
      rare: 1.0,
      epic: 1.0,
      legendary: 1.0,
      unique: 1.0,
      artifact: 1.0
    }
  };
}

/**
 * Get crafting cost multiplier for a specific rarity
 * Combines global multiplier with rarity-specific multiplier
 */
export function getCraftingCostMultiplier(rarity = 'common') {
  const rarityMultiplier = craftingCostScaling.rarityMultipliers[rarity] || 1.0;
  return craftingCostScaling.globalMultiplier * rarityMultiplier;
}

/**
 * Apply crafting cost scaling to a cost amount
 */
export function applyCraftingCostScaling(cost, rarity = 'common') {
  const multiplier = getCraftingCostMultiplier(rarity);
  return Math.floor(cost * multiplier);
}

// ============================================================================
// ENERGY REPURCHASE ESCALATION (Command-era credit sink)
// ============================================================================

/**
 * Same-day escalating pricing for energy-restoring consumables:
 * price = base * escalationFactor^(purchases today), resetting daily.
 * Converts late-game credit surplus into raid throughput at a net credit
 * loss (see docs/Roadmap-070426.md credit economy model). Factor 1.0 = off.
 */
export let energyPurchaseScaling = {
  escalationFactor: 1.0
};

export function setEnergyPurchaseScaling(config = {}) {
  energyPurchaseScaling = {
    escalationFactor: config.escalationFactor !== undefined ? config.escalationFactor : 1.0
  };
}

/**
 * Energy purchases made "today" (day boundary via toDateString; `now` is a
 * parameter so the simulator can pass simulated time).
 */
export function getEnergyPurchaseCount(player, now = Date.now()) {
  const today = new Date(now).toDateString();
  if (!player?.energyPurchases || player.energyPurchases.day !== today) return 0;
  return player.energyPurchases.count || 0;
}

/**
 * Current purchase price for a consumable. Non-energy items pass through at
 * base price; energy items escalate with today's purchase count.
 */
export function getConsumablePurchasePrice(itemData, player, now = Date.now()) {
  const base = itemData?.credits || 0;
  if (!itemData?.restoresEnergy) return base;
  const factor = energyPurchaseScaling.escalationFactor || 1.0;
  return Math.round(base * Math.pow(factor, getEnergyPurchaseCount(player, now)));
}

/**
 * Record an energy purchase against today's counter (call after a successful
 * purchase of an energy-restoring consumable).
 */
export function recordEnergyPurchase(player, now = Date.now()) {
  const today = new Date(now).toDateString();
  if (!player.energyPurchases || player.energyPurchases.day !== today) {
    player.energyPurchases = { day: today, count: 0 };
  }
  player.energyPurchases.count += 1;
}

// ============================================================================
// DROP RATE CALCULATIONS
// ============================================================================

/**
 * Calculate drop chance for a rarity at a given tier and room type
 * Exact copy from raid.js line 3167
 */
export function getRarityDropChance(rarity, tier, roomType = "gate") {
  const rarityConfig = config.itemRarities[rarity];
  if (!rarityConfig) return 0;

  const dropConfig = rarityConfig.dropChance;

  // Check minimum tier requirement
  if (dropConfig?.minTier && tier < dropConfig.minTier) return 0;

  // Handle room-type specific chances (for rare tier)
  if (dropConfig?.gate && dropConfig?.hazard) {
    const cfg = roomType === "gate" ? dropConfig.gate : dropConfig.hazard;
    return Math.min(cfg.base + (tier - 1) * cfg.perTier, cfg.max);
  }

  // Handle simple base + perTier + max formula
  if (dropConfig?.base !== undefined) {
    const chance = dropConfig.base + (tier - 1) * (dropConfig.perTier || 0);
    return dropConfig.max ? Math.min(chance, dropConfig.max) : chance;
  }

  return 0;
}

/**
 * Calculate quantity range for a rarity tier based on room type and raid tier
 * Exact copy from raid.js line 3192
 */
export function getRarityQuantityRange(rarity, tier, roomType = "gate") {
  const rarityConfig = config.itemRarities[rarity];
  if (!rarityConfig) return 1;

  const quantityConfig = rarityConfig.quantity;
  const minMaxQuantity = { min: 1, max: 1 };

  // Simple fixed quantity (epic, unique)
  if (typeof quantityConfig === "number") minMaxQuantity.min = minMaxQuantity.max = quantityConfig;

  // Room-type specific quantity
  if (quantityConfig?.gate && quantityConfig?.hazard) {
    const cfg = roomType === "gate" ? quantityConfig.gate : quantityConfig.hazard;
    minMaxQuantity.min = cfg.base ? cfg.base : 1;
    if (cfg.perTier) {
      minMaxQuantity.min = Math.min(cfg.base + (tier - 1) * cfg.perTier, cfg.max);
    }

    minMaxQuantity.max = minMaxQuantity.min;

    if (cfg.max && cfg.max > minMaxQuantity.min) {
      minMaxQuantity.max = cfg.max;
    } else if (cfg.random && cfg.random > 0) {
      minMaxQuantity.max = minMaxQuantity.min + cfg.random;
    }
  }

  return minMaxQuantity;
}

// ============================================================================
// LOOT & DATA MANIPULATION FUNCTIONS
// ============================================================================

/**
 * Add an amount to a loot object for a specific item type
 * Exact copy from raid.js line 2539
 */
export function addLoot(lootGained, itemType, amount) {
  lootGained[itemType] = (lootGained[itemType] || 0) + amount;
}

// ============================================================================
// NOTIFICATION FUNCTIONS
// ============================================================================

// Add log entry (internal export function - should generally use addRaidMessage instead)
export function addLogEntry(message, type, stepIndex = null) {
  // If stepIndex is provided, add to step-specific logs
  if (stepIndex !== null && gameState.currentRaid.raidInProgress) {
    if (!gameState.currentRaid.raidInProgress.stepLogs) {
      gameState.currentRaid.raidInProgress.stepLogs = {};
    }
    if (!gameState.currentRaid.raidInProgress.stepLogs[stepIndex]) {
      gameState.currentRaid.raidInProgress.stepLogs[stepIndex] = [];
    }
    gameState.currentRaid.raidInProgress.stepLogs[stepIndex].push({
      message,
      type,
      timestamp: Date.now(),
    });
  }
}

// Unified raid messaging export function - handles both log and notifications
export function addRaidMessage(message, type, options = {}) {
  // Add to raid log (step-specific if stepIndex provided)
  addLogEntry(message, type, options.stepIndex);
  debug(CH.raid, message);

  // Add notification unless explicitly disabled
  if (options.notify !== false) {
    // Map log types to notification types for better UX
    const notificationTypeMap = {
      failure: "error",
      info: "info",
      success: "success",
      bonus: "bonus",
    };
    const notificationType = notificationTypeMap[type] || type;

    // Use custom notification message if provided, otherwise use log message
    const notificationMessage = options.notificationMessage || message;
    addNotification(notificationMessage, notificationType);
  }
}

// ============================================================================
// FACTION & ROOM UTIL
// ============================================================================

/**
 * Helper export function to determine room's primary faction
 * Exact copy from raid.js line 3379
 */
export function getRoomFaction(room) {
  for (const [factionKey, faction] of Object.entries(config.factionSkills)) {
    if (faction.skills[room.primary]) {
      return factionKey;
    }
  }
  return null;
}

/**
 * Get faction-specific materials based on room and rarity tier
 * Exact copy from raid.js line 3362
 */
export function getFactionSpecificMaterials(room, rarity = "common") {
  const roomFaction = getRoomFaction(room);

  if (roomFaction) {
    // Get materials that match faction and rarity
    return Object.values(getAllItems())
      .filter((item) => item.type === "material" && item.rarity === rarity && item.faction === roomFaction)
      .map((item) => item.id);
  } else {
    // Mixed faction room - return all materials of this rarity
    return getItemsByType("material", rarity);
  }
}

/**
 * Get blueprints that can drop in this room. A blueprint whose artifact is
 * tied to a skill drops only in rooms using that skill (primary or support),
 * so players must target specific rooms for specific artifact plans. Utility
 * artifacts carry no skill, so their blueprints keep the wider
 * faction/neutral availability.
 */
export function getBlueprintsForRoom(room) {
  const roomFaction = getRoomFaction(room);
  const roomSkills = new Set([room.primary, ...(room.support || [])]);
  const artifactById = new Map(getAllArtifacts().map((a) => [a.id, a]));

  return Object.values(getAllItems())
    .filter((item) => {
      if (item.type !== "blueprint") return false;
      if (!item.faction) return false;
      if (item.faction !== roomFaction && item.faction !== "neutral") return false;
      const artifact = artifactById.get(item.id.replace(/-blueprint$/, ""));
      if (artifact?.skill) return roomSkills.has(artifact.skill);
      return true;
    })
    .map((item) => item.id);
}

/**
 * Helper export function to get items by category and tier from itemTable
 * Exact copy from raid.js (function getItemsByType line ~3226)
 */
export function getItemsByType(itemType = "material", rarity = null) {
  const items = Object.values(getAllItems()).filter((item) => {
    if (item.type !== itemType) return false;
    if (rarity && item.rarity !== rarity) return false;
    return true;
  });

  return items.map((item) => item.id);
}

/**
 * Convert room type to rarity tier
 * Exact copy from raid.js line 3161 (regionTierToRarity)
 */
export function regionTierToRarity(tier) {
  const rarityMap = {
    1: "common",
    2: "uncommon",
    3: "rare",
  };
  return rarityMap[tier] || "common";
}
